import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, FileText, UserCheck, Calendar, Clock } from 'lucide-react';

const backendUrl =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:8001'
    : '');

function formatDateBR(value) {
  if (!value) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-');
    return `${d}/${m}/${y}`;
  }
  try {
    return new Date(value).toLocaleDateString('pt-BR');
  } catch {
    return value;
  }
}

export default function PublicEvolutionAcknowledgementPage() {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [data, setData] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError('');

        const res = await fetch(`${backendUrl}/api/appointments/public/acknowledgement/${token}`);

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || 'Link inválido ou indisponível.');
        }

        const json = await res.json();
        setData(json);
        setConfirmed(json.patient_acknowledgement_status === 'ciente');
      } catch (e) {
        setError(e.message || 'Erro ao carregar evolução clínica.');
      } finally {
        setLoading(false);
      }
    }

    if (token) load();
  }, [token]);

  const handleConfirm = async () => {
    try {
      setConfirming(true);
      setError('');

      const res = await fetch(`${backendUrl}/api/appointments/public/acknowledgement/${token}/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Erro ao registrar ciência.');
      }

      setConfirmed(true);
    } catch (e) {
      setError(e.message || 'Erro ao registrar ciência.');
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow p-6 text-gray-700">
          Carregando evolução clínica...
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow p-6 max-w-lg w-full">
          <div className="flex items-center gap-2 text-red-600 mb-3">
            <AlertCircle className="w-5 h-5" />
            <h1 className="font-semibold">Não foi possível abrir este link</h1>
          </div>
          <p className="text-sm text-gray-700">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow border overflow-hidden">
        <div className="bg-purple-700 text-white p-6">
          <div className="flex items-center gap-3">
            <FileText className="w-7 h-7" />
            <div>
              <h1 className="text-xl font-bold">Ciência de Evolução Clínica</h1>
              <p className="text-sm text-purple-100">
                Leia as informações abaixo antes de confirmar ciência.
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {confirmed && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3 text-green-800">
              <CheckCircle className="w-5 h-5 mt-0.5" />
              <div>
                <p className="font-semibold">Ciência já registrada.</p>
                <p className="text-sm">Esta confirmação ficou registrada no prontuário.</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="border rounded-xl p-3">
              <p className="text-gray-500 text-xs">Paciente</p>
              <p className="font-medium text-gray-900">{data?.patient_name || '—'}</p>
            </div>

            <div className="border rounded-xl p-3">
              <p className="text-gray-500 text-xs">Procedimento / atendimento</p>
              <p className="font-medium text-gray-900">{data?.procedure || '—'}</p>
            </div>

            <div className="border rounded-xl p-3 flex gap-2 items-center">
              <Calendar className="w-4 h-4 text-gray-500" />
              <div>
                <p className="text-gray-500 text-xs">Data</p>
                <p className="font-medium text-gray-900">{formatDateBR(data?.appointment_date)}</p>
              </div>
            </div>

            <div className="border rounded-xl p-3 flex gap-2 items-center">
              <Clock className="w-4 h-4 text-gray-500" />
              <div>
                <p className="text-gray-500 text-xs">Horário</p>
                <p className="font-medium text-gray-900">{data?.start_time || '—'}</p>
              </div>
            </div>

            <div className="border rounded-xl p-3 md:col-span-2 flex gap-2 items-start">
              <UserCheck className="w-4 h-4 text-gray-500 mt-0.5" />
              <div>
                <p className="text-gray-500 text-xs">Profissional responsável</p>
                <p className="font-medium text-gray-900">
                  {data?.professional_name || '—'}
                  {data?.professional_cro ? ` • ${data.professional_cro}` : ''}
                </p>
              </div>
            </div>
          </div>

          <div>
            <p className="font-semibold text-gray-900 mb-2">Evolução clínica registrada</p>
            <div className="border rounded-xl p-4 bg-gray-50 text-sm text-gray-800 whitespace-pre-wrap">
              {data?.evolution_notes || 'Nenhuma evolução clínica informada.'}
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm text-yellow-900">
            Ao confirmar, você declara que teve acesso ao conteúdo desta evolução clínica.
            Esta confirmação não substitui assinatura formal em documentos que exijam assinatura específica.
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={handleConfirm}
            disabled={confirming || confirmed}
            className="w-full bg-purple-700 text-white rounded-xl py-3 font-semibold hover:bg-purple-800 disabled:opacity-60"
          >
            {confirmed ? 'Ciência registrada' : confirming ? 'Registrando...' : 'Li e estou ciente'}
          </button>
        </div>
      </div>
    </div>
  );
}